// client/src/pages/seo/DigitaleSpeisekartePizzeriaDe.jsx
import React, { useEffect } from "react";
import SeoRelatedLinks from "../../components/seo/SeoRelatedLinks.jsx";
import SeoHeader from "../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../components/seo/SeoWorkSteps.jsx";

const sections = [
  {
    id: "start",
    label: "Eine Pizzeria braucht eine Speisekarte, die Varianten klar darstellt",
  },
  {
    id: "why",
    label: "Warum ist eine digitale Speisekarte für Pizzerien sinnvoll?",
  },
  {
    id: "structure",
    label: "Wie sollte eine digitale Speisekarte für eine Pizzeria aufgebaut sein?",
  },
  {
    id: "sizes",
    label: "Wie sollten Pizzagrößen, Extras und Toppings dargestellt werden?",
  },
  {
    id: "photos",
    label: "Sollte eine Pizzeria Bilder in der digitalen Speisekarte nutzen?",
  },
  {
    id: "mistakes",
    label: "Welche Fehler passieren bei digitalen Speisekarten für Pizzerien?",
  },
  {
    id: "recommendation",
    label: "Wie würde ich als Pizzeria-Inhaber starten?",
  },
  {
    id: "next-steps",
    label: "Was sollten Sie jetzt tun?",
  },
  {
    id: "bestplace",
    label: "Wie Best Place hilft, eine digitale Pizzeria-Speisekarte zu erstellen",
  },
  {
    id: "faq",
    label: "FAQ zu digitalen Speisekarten für Pizzerien",
  },
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Idee für Illustration">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>
      <div>
        <strong>Idee für Illustration:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export default function DigitaleSpeisekartePizzeriaDe() {
  useEffect(() => {
    document.documentElement.setAttribute("lang", "de");
    document.title =
      "Digitale Speisekarte für Pizzerien: Größen, Toppings und Angebote online | Best Place";
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="de"
        langLinks={{
          en: "/en/digital-menu-pizzeria",
          de: "/de/digitale-speisekarte-pizzeria",
          no: "/no/digital-meny-pizzeria",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Digitale Speisekarte für Pizzerien: Pizzagrößen, Toppings und
              Angebote online verwalten
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                Eine Pizzeria hat oft mehr Varianten als eine klassische
                Restaurant-Speisekarte: verschiedene Pizzagrößen, zusätzliche
                Toppings, vegetarische Optionen, Familienpizza, Mittagsangebote,
                Pasta, Salate, Desserts und Getränke. Genau deshalb kann eine
                digitale Speisekarte hier besonders nützlich sein.
              </p>

              <p className="seoHero__text">
                Statt mehrere PDF-Dateien, gedruckte Flyer oder veraltete
                Speisekarten zu pflegen, kann die Pizzeria Gerichte, Preise,
                Extras und Angebote online aktualisieren. Der QR-Code bleibt
                gleich, während die Speisekarte dahinter aktuell bleibt.
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="de" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Inhalt des Artikels</h2>

        <nav className="qrArticle__nav" aria-label="Artikelnavigation">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="start" className="acquaintance">
          <h2>
            Eine Pizzeria braucht eine Speisekarte, die Varianten klar darstellt
          </h2>

          <p>
            Für eine Pizzeria reicht es oft nicht aus, einfach eine Liste mit
            Gerichten online zu stellen. Gäste möchten wissen: Welche Größe ist
            verfügbar? Welche Toppings können hinzugefügt werden? Gibt es
            vegetarische Optionen? Können Zutaten geändert werden? Welche
            Angebote gibt es heute?
          </p>

          <p>
            Eine digitale Pizzeria-Speisekarte sollte diese Optionen leichter
            verständlich machen. Sie sollte Gästen helfen, Pizza, Pasta,
            Getränke, Angebote, Größen und Extras zu finden, ohne eine
            überladene PDF-Datei oder einen veralteten Flyer lesen zu müssen.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>Schwächer</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Lange PDF-Speisekarte mit kleinen Preistabellen.
                </li>
                <li className="text__seo-li">
                  Größen und Extras sind schwer verständlich.
                </li>
                <li className="text__seo-li">
                  Alte Flyer bleiben online.
                </li>
                <li className="text__seo-li">
                  Nicht verfügbare Produkte bleiben sichtbar.
                </li>
                <li className="text__seo-li">
                  Mittagsangebote werden nicht aktualisiert.
                </li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Besser</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Klare Kategorien für Pizza, Pasta, Salate und Getränke.
                </li>
                <li className="text__seo-li">
                  Größen und Extras werden übersichtlich dargestellt.
                </li>
                <li className="text__seo-li">
                  Preise werden online aktualisiert.
                </li>
                <li className="text__seo-li">
                  Nicht verfügbare Gerichte können ausgeblendet werden.
                </li>
                <li className="text__seo-li">Der QR-Code bleibt gleich.</li>
              </ul>
            </div>
          </div>

          <h3>Starten Sie mit einer kleinen digitalen Pizza-Speisekarte</h3>

          <p>
            Fügen Sie zuerst Ihre beliebtesten Pizzen, Größen und einige Extras
            hinzu. Prüfen Sie danach die Smartphone-Ansicht über den QR-Code.
            Ein kleiner realistischer Test ist besser, als vom ersten Tag an
            jede Variante perfekt digitalisieren zu wollen.
          </p>
        </section>

        <section id="why" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Warum ist eine digitale Speisekarte für Pizzerien sinnvoll?</h2>

            <p>
              Pizzerien haben oft eine besondere Menüstruktur. Eine Pizza kann
              in mehreren Größen angeboten werden, Toppings können ergänzt
              werden, manche Zutaten kosten extra und bestimmte Gerichte sind
              vielleicht nur zu bestimmten Zeiten verfügbar.
            </p>

            <p>
              Auf Papier oder in einer PDF-Datei kann diese Struktur schnell
              überladen wirken. In einer digitalen Speisekarte lassen sich
              Kategorien, Varianten und Angebote klarer voneinander trennen.
            </p>

            <h3>Digitale Struktur hilft bei der Komplexität einer Pizzeria</h3>

            <p>
              Eine digitale Speisekarte ist nicht nur eine moderne Darstellung.
              Sie hilft, typische Pizzeria-Komplexität für Gäste verständlicher
              zu machen: Größen, Extras, Toppings, Angebote, vegetarische
              Optionen und Verfügbarkeit.
            </p>

            <p>
              Außerdem hilft sie der Pizzeria, Preise und Angebote aktuell zu
              halten, ohne ständig Dateien, Flyer oder gedruckte Speisekarten
              ersetzen zu müssen.
            </p>
          </div>
        </section>

        <section id="structure" className="acquaintance">
          <h2>
            Wie sollte eine digitale Speisekarte für eine Pizzeria aufgebaut
            sein?
          </h2>

          <p>
            Eine gute Pizzeria-Speisekarte braucht klare Kategorien. Wenn Pizza,
            Pasta, Salate, Desserts, Getränke, Angebote und Extras in einer
            einzigen langen Liste erscheinen, verlieren Gäste schnell die
            Orientierung.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Klassische Pizzen.</li>
            <li className="text__seo-li">Spezialpizzen.</li>
            <li className="text__seo-li">Vegetarische Pizza.</li>
            <li className="text__seo-li">Pasta.</li>
            <li className="text__seo-li">Salate.</li>
            <li className="text__seo-li">Desserts.</li>
            <li className="text__seo-li">Getränke.</li>
            <li className="text__seo-li">Mittagsangebote.</li>
          </ul>

          <p>
            Die Reihenfolge ist wichtig. Bestseller und einfache Kategorien
            sollten zuerst sichtbar sein. Extras und Sonderoptionen sollten den
            Hauptfluss der Speisekarte nicht unterbrechen.
          </p>

          <VisualIdea>
            Smartphone mit Pizzeria-Speisekarte: klassische Pizzen,
            Spezialitäten, Pasta, Salate, Getränke und ein sichtbarer Bereich
            für Angebote.
          </VisualIdea>
        </section>

        <section id="sizes" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>
              Wie sollten Pizzagrößen, Extras und Toppings dargestellt werden?
            </h2>

            <p>
              Die größte Herausforderung bei Pizzeria-Speisekarten sind oft
              Varianten: klein, normal, groß, Familiengröße, extra Käse,
              zusätzliche Toppings, scharfe Option, vegetarische Option oder
              entfernte Zutaten.
            </p>

            <p>
              Gäste sollten diese Optionen schnell verstehen, ohne eine
              komplizierte Preistabelle lesen zu müssen.
            </p>

            <ol className="seoSteps">
              <li className="text__seo-li">
                <strong>Größen klar benennen:</strong> Zum Beispiel 26 cm, 32 cm
                oder Familienpizza.
              </li>

              <li className="text__seo-li">
                <strong>Preislogik einfach halten:</strong> Varianten sollten
                sich nicht wie versteckte Kosten anfühlen.
              </li>

              <li className="text__seo-li">
                <strong>Extras separat erklären:</strong> Extra Käse, Gemüse,
                Fleisch oder Sauce sollten leicht verständlich bleiben.
              </li>

              <li className="text__seo-li">
                <strong>Beliebte Optionen zeigen:</strong> Zum Beispiel
                vegetarisch, scharf oder extra Käse.
              </li>

              <li className="text__seo-li">
                <strong>Verfügbarkeit verwalten:</strong> Wenn ein Topping nicht
                verfügbar ist, sollte es nicht prominent sichtbar bleiben.
              </li>
            </ol>

            <h3>Je mehr Varianten, desto klarer muss die Struktur sein</h3>

            <p>
              Wenn ein Gericht viele mögliche Optionen hat, sollte die digitale
              Speisekarte diese Informationen nicht in einem unübersichtlichen
              Textblock verstecken. Größen, Extras und Verfügbarkeit sollten
              leicht zu erfassen und zu verstehen sein.
            </p>
          </div>
        </section>

        <section id="photos" className="acquaintance">
          <h2>
            Sollte eine Pizzeria Bilder in der digitalen Speisekarte nutzen?
          </h2>

          <p>
            Bilder können für Pizzerien sehr hilfreich sein, aber nicht jede
            Pizza braucht sofort ein Foto. Gute Bilder sind besonders nützlich
            für Spezialitäten, neue Angebote, Desserts oder Gerichte, die Gäste
            noch nicht gut kennen.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Bestseller zeigen.</li>
            <li className="text__seo-li">Spezialpizza hervorheben.</li>
            <li className="text__seo-li">Desserts präsentieren.</li>
            <li className="text__seo-li">Mittagsangebote sichtbar machen.</li>
            <li className="text__seo-li">Realistische Portionen zeigen.</li>
            <li className="text__seo-li">Einheitlichen Fotostil nutzen.</li>
            <li className="text__seo-li">Sehr große Dateien vermeiden.</li>
            <li className="text__seo-li">Alte Fotos ersetzen.</li>
          </ul>

          <p>
            Für eine Pizzeria ist ein realistisches Foto oft stärker als ein
            perfektes Werbebild. Gäste sollten ungefähr das erwarten können, was
            später auf dem Teller oder in der Box ankommt.
          </p>

          <VisualIdea>
            Eine digitale Pizzeria-Speisekarte mit drei ausgewählten Bildern:
            ein Bestseller, eine Spezialpizza und ein Dessert. Die Fotos sind
            hell, realistisch und einheitlich.
          </VisualIdea>
        </section>

        <section id="mistakes" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>
              Welche Fehler passieren bei digitalen Speisekarten für Pizzerien?
            </h2>

            <p>
              Viele Fehler entstehen, wenn eine digitale Speisekarte wie ein
              alter Flyer behandelt wird. Eine Pizzeria braucht eine klare
              digitale Struktur, damit Varianten und Extras verständlich bleiben.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Häufige Fehler</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Alle Pizzen erscheinen in einer einzigen langen Liste.
                  </li>
                  <li className="text__seo-li">
                    Größen und Preise sind schwer lesbar.
                  </li>
                  <li className="text__seo-li">
                    Extras sind unklar oder werden zu spät erklärt.
                  </li>
                  <li className="text__seo-li">
                    Alte Flyer-PDFs bleiben online.
                  </li>
                  <li className="text__seo-li">
                    Nicht verfügbare Toppings bleiben sichtbar.
                  </li>
                  <li className="text__seo-li">
                    Fotos passen nicht zur echten Pizza.
                  </li>
                  <li className="text__seo-li">
                    Mittagsangebote werden nicht aktualisiert.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Besser</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">Klare Kategorien verwenden.</li>
                  <li className="text__seo-li">
                    Größen logisch darstellen.
                  </li>
                  <li className="text__seo-li">
                    Extras separat und klar erklären.
                  </li>
                  <li className="text__seo-li">
                    Eine zentrale digitale Speisekarte pflegen.
                  </li>
                  <li className="text__seo-li">
                    Nicht verfügbare Zutaten ausblenden.
                  </li>
                  <li className="text__seo-li">
                    Realistische Bilder verwenden.
                  </li>
                  <li className="text__seo-li">
                    Angebote regelmäßig aktualisieren.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="recommendation" className="acquaintance">
          <h2>Wie würde ich als Pizzeria-Inhaber starten?</h2>

          <p>
            Ich würde nicht versuchen, die gesamte Speisekarte mit jeder Variante
            vom ersten Tag an perfekt abzubilden. Ich würde mit dem Kernangebot
            starten und die Struktur danach erweitern.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">
              <strong>Mit Bestsellern starten:</strong> 10 bis 15 beliebte
              Pizzen hinzufügen.
            </li>

            <li className="text__seo-li">
              <strong>Größen klar definieren:</strong> Zum Beispiel normal,
              groß und Familiengröße.
            </li>

            <li className="text__seo-li">
              <strong>Extras zuerst begrenzen:</strong> Am Anfang nur die
              wichtigsten Zusatzoptionen hinzufügen.
            </li>

            <li className="text__seo-li">
              <strong>Eine Angebotskategorie testen:</strong> Zum Beispiel
              Mittagsmenü oder Familienangebot.
            </li>

            <li className="text__seo-li">
              <strong>Auf dem Smartphone prüfen:</strong> Gäste sollten schnell
              verstehen, was sie wählen können.
            </li>

            <li className="text__seo-li">
              <strong>Später erweitern:</strong> Pasta, Salate, Desserts und
              Getränke anschließend hinzufügen.
            </li>
          </ol>

          <p>
            So bleibt die digitale Speisekarte klar und kann aus dem echten
            Restaurantablauf heraus wachsen.
          </p>
        </section>

        <section id="next-steps" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Was sollten Sie jetzt tun?</h2>

            <p>
              Wenn Sie eine digitale Speisekarte für Ihre Pizzeria erstellen
              möchten, starten Sie mit einem kleinen realistischen Test.
            </p>

            <ul className="qrArticle__block-li">
              <li className="text__seo-li">Eine digitale Speisekarte erstellen.</li>
              <li className="text__seo-li">
                Kategorien für Pizza, Pasta und Getränke hinzufügen.
              </li>
              <li className="text__seo-li">
                10 bis 15 beliebte Pizzen eintragen.
              </li>
              <li className="text__seo-li">
                Größen und die wichtigsten Extras hinzufügen.
              </li>
              <li className="text__seo-li">
                Die Speisekarte auf einem Smartphone öffnen.
              </li>
              <li className="text__seo-li">
                Den QR-Code im Restaurant testen.
              </li>
              <li className="text__seo-li">
                Angebote, Fotos und weitere Kategorien später erweitern.
              </li>
            </ul>

            <VisualIdea>
              Ein kleines Testmenü für eine Pizzeria: 10 beliebte Pizzen, klare
              Größen, einige Extras, eine Angebotskategorie und eine
              Smartphone-Ansicht über den QR-Code.
            </VisualIdea>
          </div>
        </section>

        <div id="bestplace" className="qrArticle__anchorTarget">
          <SeoWorkSteps lang="de" />
        </div>

        <section id="faq" className="acquaintance">
          <h2>FAQ zu digitalen Speisekarten für Pizzerien</h2>

          <div className="seoFaq">
            <details>
              <summary>Was ist eine digitale Speisekarte für Pizzerien?</summary>
              <p>
                Es ist eine online verwaltete Speisekarte für Pizza, Pasta,
                Salate, Getränke, Angebote, Größen und Extras, die Gäste per
                QR-Code öffnen können.
              </p>
            </details>

            <details>
              <summary>Kann ich Pizzagrößen anzeigen?</summary>
              <p>
                Ja. Größen wie klein, normal, groß oder Familiengröße können mit
                passenden Preisen übersichtlich dargestellt werden.
              </p>
            </details>

            <details>
              <summary>Kann ich Extras und zusätzliche Toppings anzeigen?</summary>
              <p>
                Ja. Extras sollten klar dargestellt werden und nicht zu
                kompliziert wirken, damit Gäste verstehen, was möglich ist.
              </p>
            </details>

            <details>
              <summary>
                Muss ich den QR-Code neu drucken, wenn sich Preise ändern?
              </summary>
              <p>
                Nein, wenn der QR-Code auf einen stabilen Menü-Link führt.
                Preise können online aktualisiert werden, während der QR-Code
                gleich bleibt.
              </p>
            </details>

            <details>
              <summary>Sollte jede Pizza ein Bild haben?</summary>
              <p>
                Nicht unbedingt. Für den ersten Schritt reichen Bilder für
                Bestseller, Spezialitäten, Angebote oder neue Gerichte aus.
              </p>
            </details>

            <details>
              <summary>Kann ich kostenlos starten?</summary>
              <p>
                Ja. Für einen ersten Test reichen einige beliebte Pizzen,
                Größen, Preise und eine QR-Code-Prüfung auf dem Smartphone aus.
              </p>
            </details>
          </div>
        </section>
        <SeoRelatedLinks
          lang="de"
          group="menu"
          exclude="/de/digitale-speisekarte-pizzeria"
        />
      </article>
    </main>
  );
}